import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import classNames from "classnames";
import { useEffect, useState } from "react";

import "dayjs/locale/da";
import "dayjs/locale/de";
import "dayjs/locale/fi";
import "dayjs/locale/fr";
import "dayjs/locale/nn";
import "dayjs/locale/sv";

import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { Alert, SelectChangeEvent } from "@mui/material";

import styles from "./complaint-forms.module.scss";
import TextField from "@/components/atoms/text-field/text-field";
import FormControlLabel from "@/components/atoms/form-label/label";
import Button from "@/components/atoms/button/button";
import { Select, Option } from "@/components/atoms/select-dropdown/select";
import StepBox from "@/components/atoms/step-box/step-box";

import TransactionsStep from "@/components/molecules/transactions/transactions";
import CustomerDetailsStep from "@/components/molecules/customer-details/customer-details";
import SummaryStep from "@/components/molecules/summary/summary";
import {
  usePrefilledStatus,
  getPrefilledData,
  canSkipPrefilledGroup,
} from "@/helpers/prefill-hook";
import { getSupportEmail } from "@/helpers/support-email";
import setCsrfCookie from "@/helpers/csrfHelper";

import DisputePage from "@/components/organisms/dispute-page/page";

import { GetServerSidePropsContext } from "next";
import InputFile from "@/components/atoms/input-file/input-file";
import { goToPage } from "@/helpers/stepHelper";

import PageSetup from "@/helpers/PageSetup";
import { client, server } from "@/helpers/logger";

let lastLog = "";
let isInitialPrefilled = true;

function PageIncorrect(props: Readonly<{ locale: string; csrfToken: string }>) {
  const {
    t,
    brand,
    data,
    country,
    mailto,
    isDevelopment,
    activeStep,
    setActiveStep,
    succeededStep,
    setSucceededStep,
    isSessionExpired,
  } = PageSetup(props);

  const title: string = `${t("Cardholder dispute")} | ${t(
    "Something went wrong with my purchase or withdrawal",
  )}`;

  const [cardEvent, setCardEvent] = useState("");
  // @ts-ignore
  usePrefilledStatus("what-happened[what-happened]", data, (val: string) => {
    if (!cardEvent.length && val?.length) {
      setCardEvent(val);
    }
  });

  const [atmEvent, setAtmEvent] = useState("");
  usePrefilledStatus(
    "what-happened[what-happened-at-the-atm]",
    data,
    // @ts-ignore
    (val: string) => {
      if (!atmEvent.length && val?.length) {
        setAtmEvent(val);
      }
    },
  );

  const [purchasedProduct, setPurchasedProduct] = useState("");
  usePrefilledStatus(
    "what-happened[what-did-you-purchase]",
    data,
    // @ts-ignore
    (val: string) => {
      if (!purchasedProduct.length && val?.length) {
        setPurchasedProduct(val);
      }
    },
  );

  const [haveThirtyDaysPassed, setHaveThirtyDaysPassed] = useState("");
  usePrefilledStatus(
    "what-happened[have-thirty-days-passed]",
    data,
    // @ts-ignore
    (val: string) => {
      if (!haveThirtyDaysPassed.length && val?.length) {
        setHaveThirtyDaysPassed(val);
      }
    },
  );

  const supportEmail = getSupportEmail(
    `${brand}`,
    country ? `${country}` : undefined,
    props.locale,
    "incorrect",
  );

  const emailTo = mailto || supportEmail;

  if (mailto && supportEmail !== lastLog) {
    client.log("SupportEmail", supportEmail);
    client.log("TestingEmail", mailto);
    lastLog = supportEmail;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isInitialPrefilled && isDevelopment) {
      // no customer data in prod logs ergo not able to skip
      if (canSkipPrefilledGroup("what-happened[what-happened]", data, 1)) {
        const form: HTMLFormElement = document.querySelector(
          "form",
        ) as HTMLFormElement;
        if (form && form.checkValidity() && activeStep <= 0) {
          setActiveStep(1);
          setSucceededStep(1);
          isInitialPrefilled = false;
        }
      }
    }
  });

  const whatHappened = (
    <>
      <input type="hidden" name="email-subject" value={cardEvent} />
      <input type="hidden" name="mailto" value={emailTo} />

      {haveThirtyDaysPassed == "No" && (
        <Alert
          severity="error"
          role="alert"
          tabIndex={-1}
          id="has-thirty-days-passed"
        >
          {t(
            "We cannot help you to proceed with a complaint until 30 days from the date of purchase has passed or after the agreed delivery with the seller. You are welcome to return to us when the time requriment has passed.",
          )}
        </Alert>
      )}

      <FormControlLabel
        labelPlacement="top"
        control={
          <Select
            value={cardEvent}
            name="what-happened[what-happened]"
            required={activeStep === 0}
            onChange={(e: SelectChangeEvent<unknown>) => {
              setCardEvent((e.target as unknown as HTMLOptionElement).value);
            }}
          >
            <Option value="" hidden disabled>
              {t("Choose")}
            </Option>
            <Option
              value={
                "My account has been charged with the same amount several times"
              }
            >
              {t(
                "My account has been charged with the same amount several times",
              )}
            </Option>
            <Option value={"An error has occurred with my cash withdrawal"}>
              {t("An error has occurred with my cash withdrawal")}
            </Option>
            <Option value={"The amount charged does not match my receipt"}>
              {t("The amount charged does not match my receipt")}
            </Option>
            <Option
              value={
                "My card has been charged even though I paid by other means"
              }
            >
              {t("My card has been charged even though I paid by other means")}
            </Option>
            <Option
              value={
                "There is something wrong with the product or service I have purchased"
              }
            >
              {t(
                "There is something wrong with the product or service I have purchased",
              )}
            </Option>
            <Option
              value={
                "I have not received the product or service that I have purchased"
              }
            >
              {t(
                "I have not received the product or service that I have purchased",
              )}
            </Option>
            <Option
              value={"I have cancelled my subscription but still get charged"}
            >
              {t("I have cancelled my subscription but still get charged")}
            </Option>
            <Option value={"I have not received my credit transaction"}>
              {t("I have not received my credit transaction")}
            </Option>
            <Option
              value={
                "The seller has declared bankruptcy and I will therefore not receive the product or service"
              }
            >
              {t(
                "The seller has declared bankruptcy and I will therefore not receive the product or service",
              )}
            </Option>
          </Select>
        }
        label={
          <Typography variant="body1" component="span">
            {t("Please choose the best alternative to describe your case")}
          </Typography>
        }
      />

      {cardEvent ===
        "My account has been charged with the same amount several times" ||
      cardEvent === "The amount charged does not match my receipt" ||
      cardEvent ===
        "My card has been charged even though I paid by other means" ? (
        <>
          {cardEvent === "The amount charged does not match my receipt" ||
          cardEvent ===
            "My card has been charged even though I paid by other means" ? (
            <InputFile
              label={
                <Typography variant="body1" component="span">
                  {t("Attach your receipt")}
                </Typography>
              }
              required={activeStep === 0}
              name="what-happened[attach-your-receipt]"
              title={t("Attach your receipt") || "Attach your receipt"}
            />
          ) : null}
        </>
      ) : null}
      {cardEvent === "An error has occurred with my cash withdrawal" ? (
        <FormControlLabel
          labelPlacement="top"
          control={
            <Select
              value={atmEvent}
              onChange={(e: SelectChangeEvent<unknown>) => {
                setAtmEvent((e.target as unknown as HTMLOptionElement).value);
              }}
              name="what-happened[what-happened-at-the-atm]"
              required={activeStep === 0}
            >
              <Option value="" hidden disabled>
                {t("Choose")}
              </Option>
              <Option
                value={
                  "I made a cash withdrawal, but was charged several times"
                }
              >
                {t("I made a cash withdrawal, but was charged several times")}
              </Option>
              <Option value={"The ATM dispensed the wrong amount"}>
                {t("The ATM dispensed the wrong amount")}
              </Option>
              <Option value={"I did not receive any money"}>
                {t("I did not receive any money")}
              </Option>
            </Select>
          }
          label={
            <Typography variant="body1" component="span">
              {t("What happened at the ATM")}
            </Typography>
          }
        />
      ) : null}
      {cardEvent ===
      "There is something wrong with the product or service I have purchased" ? (
        <>
          <FormControlLabel
            labelPlacement="top"
            control={
              <Select
                value={purchasedProduct}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  setPurchasedProduct(
                    (e.target as unknown as HTMLOptionElement).value,
                  );
                }}
                name="what-happened[what-did-you-purchase]"
                required={activeStep === 0}
              >
                <Option value="" hidden disabled>
                  {t("Choose")}
                </Option>
                <Option value={"Product (a physical object)"}>
                  {t("Product (a physical object)")}
                </Option>
                <Option
                  value={
                    "Service (e.g. airline tickets, hotel bookings, software etc.)"
                  }
                >
                  {t(
                    "Service (e.g. airline tickets, hotel bookings, software etc.)",
                  )}
                </Option>
              </Select>
            }
            label={
              <Typography variant="body1" component="span">
                {t("What did you purchase")}
              </Typography>
            }
          />
          {purchasedProduct === "Product (a physical object)" ? (
            <FormControlLabel
              labelPlacement="top"
              control={
                <Select
                  defaultValue={getPrefilledData(
                    "what-happened[the-product-you-purchased]",
                    data,
                  )}
                  name="what-happened[the-product-you-purchased]"
                  required={activeStep === 0}
                >
                  <Option value="" hidden disabled>
                    {t("Choose")}
                  </Option>
                  <Option value={"is a forgery"}>{t("is a forgery")}</Option>
                  <Option value={"does not match the description"}>
                    {t("does not match the description")}
                  </Option>
                  <Option value={"is defective"}>{t("is defective")}</Option>
                </Select>
              }
              label={
                <Typography variant="body1" component="span">
                  {t("The product you purchased")}
                </Typography>
              }
            />
          ) : null}
          <InputFile
            label={
              <Typography variant="body1" component="span">
                {purchasedProduct === "Product (a physical object)"
                  ? t("Attach an order confirmation")
                  : t("Attach an order or booking confirmation")}
              </Typography>
            }
            required={activeStep === 0}
            name="what-happened[attach-an-order-or-booking-confirmation]"
            title={
              (purchasedProduct === "Product (a physical object)"
                ? t("Attach an order confirmation")
                : t("Attach an order or booking confirmation")) ||
              "Attach an order or booking confirmation"
            }
          />
        </>
      ) : null}

      {cardEvent === "I have cancelled my subscription but still get charged" ||
      cardEvent === "I have not received my credit transaction" ||
      cardEvent ===
        "The seller has declared bankruptcy and I will therefore not receive the product or service" ? (
        <InputFile
          label={
            <Typography variant="body1" component="span">
              {cardEvent ===
              "I have cancelled my subscription but still get charged"
                ? t("Attach confirmation or proof of cancellation here")
                : null}
              {cardEvent === "I have not received my credit transaction"
                ? t(
                    "Attach a credit receipt or documentation confirming the credit transaction",
                  )
                : null}
              {cardEvent ===
              "The seller has declared bankruptcy and I will therefore not receive the product or service"
                ? t("Attach an order or booking confirmation")
                : null}
            </Typography>
          }
          required={activeStep === 0}
          name="what-happened[attach-an-order-or-booking-confirmation]"
        />
      ) : null}

      {cardEvent ===
      "The seller has declared bankruptcy and I will therefore not receive the product or service" ? (
        <InputFile
          label={
            <Typography variant="body1" component="span">
              {t("Attach documentation confirming the bankruptcy here")}
            </Typography>
          }
          required={activeStep === 0}
          name="what-happened[documentation-confirming-bankrupcy]"
        />
      ) : null}

      {cardEvent ===
        "I have not received the product or service that I have purchased" && (
        <FormControlLabel
          labelPlacement="top"
          control={
            <Select
              aria-describedby="has-thirty-days-passed"
              value={haveThirtyDaysPassed}
              name="what-happened[have-thirty-days-passed]"
              inputProps={{
                required: activeStep === 0,
                pattern: "Yes",
              }}
              onChange={(e: SelectChangeEvent<unknown>) => {
                setHaveThirtyDaysPassed(
                  (e.target as unknown as HTMLOptionElement).value,
                );
              }}
            >
              <Option value="" hidden disabled>
                {t("Choose")}
              </Option>
              <Option value={"Yes"}>{t("Yes")}</Option>
              <Option value={"No"}>{t("No")}</Option>
            </Select>
          }
          label={
            <Typography variant="body1" component="span">
              {t("Has it been 30 days since you placed the order?") ??
                "Has it been 30 days since you placed the order?"}
            </Typography>
          }
        />
      )}

      {cardEvent.length ? (
        <>
          <FormControlLabel
            labelPlacement="top"
            control={
              <TextField
                rows={8}
                multiline
                inputProps={{
                  required: activeStep === 0,
                }}
                autoComplete="off"
                name="what-happened[describe-what-happened]"
                defaultValue={
                  isDevelopment
                    ? getPrefilledData(
                        "what-happened[describe-what-happened]",
                        data,
                      )
                    : "" /* no personal data in prod */
                }
              />
            }
            label={
              <Typography variant="body1" component="span">
                {t("Describe what happened")}
              </Typography>
            }
          />
          <FormControlLabel
            labelPlacement="top"
            control={
              <TextField
                rows={8}
                multiline
                required={
                  cardEvent ===
                    "I have cancelled my subscription but still get charged" &&
                  activeStep === 0
                }
                autoComplete="off"
                name="what-happened[attach-communication]"
                defaultValue={
                  isDevelopment
                    ? getPrefilledData(
                        "what-happened[attach-communication]",
                        data,
                      )
                    : "" /* no personal data in prod */
                }
                helperText={t(
                  "If you have had a dialogue with the merchant e.g. through e-mail, you can paste the text below",
                )}
              />
            }
            label={
              <Typography variant="body1" component="span">
                {cardEvent ===
                "I have cancelled my subscription but still get charged"
                  ? `${t("Attach confirmation or proof of cancellation here")}`
                  : `${t("Attach communication")} ${t("(Optional)")}`}
              </Typography>
            }
          />

          <InputFile
            label={
              <Typography variant="body1" component="span">
                {t(
                  "Attach screenshots or relevant files related to the disputed transaction, such as order confirmations, communication with the merchant, or any necessary documentation (Optional)",
                )}
              </Typography>
            }
            multiple={true}
            name="what-happened[attached-files]"
            title={
              t(
                "Attach screenshots or relevant files related to the disputed transaction, such as order confirmations, communication with the merchant, or any necessary documentation (Optional)",
              ) ??
              "Attach screenshots or relevant files related to the disputed transaction, such as order confirmations, communication with the merchant, or any necessary documentation (Optional)"
            }
          />
        </>
      ) : null}
    </>
  );

  return (
    /* @ts-ignore */
    <DisputePage
      title={title}
      csrfToken={props.csrfToken}
      locale={props.locale}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      succeededStep={succeededStep}
      setSucceededStep={setSucceededStep}
      form="incorrect"
      isSessionExpired={isSessionExpired}
      header={
        <>
          <Typography
            variant="h5"
            component="h1"
            className={classNames([styles.h1])}
            tabIndex={-1}
          >
            {t("Something went wrong with your purchase or withdrawal")}
          </Typography>
          <Typography variant="body1" className={classNames([styles.h2])}>
            {t(
              "To proceed with your dispute, you have to fill out the information below",
            )}
          </Typography>
        </>
      }
      childrenStepsDesktop={
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step key={t("What happened")} completed={activeStep > 0}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("What happened")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Transaction details")} completed={activeStep > 1}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Transaction details")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Customer information")} completed={activeStep > 2}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Customer information")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Summary")} completed={activeStep > 3}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Summary")}
              </Typography>
            </StepLabel>
          </Step>
        </Stepper>
      }
      childrenDesktop={
        <>
          <div style={{ display: activeStep !== 0 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {whatHappened}
              <StepBox>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep !== 1 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 0 ? (
                <TransactionsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  setSucceededStep={setSucceededStep}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep !== 2 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 1 ? (
                <CustomerDetailsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  succeededStep={succeededStep}
                  setSucceededStep={setSucceededStep}
                  brand={brand}
                  locale={props.locale}
                  page={"incorrect"}
                  country={country}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                >
                  {t("Back")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep !== 3 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 2 ? (
                <SummaryStep
                  setActiveStep={setActiveStep}
                  activeStep={activeStep}
                  data={data}
                  brand={brand}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                >
                  {t("Back")}
                </Button>
                <Button type="submit" variant="contained">
                  {t("Send")}
                </Button>
              </StepBox>
            </Stack>
          </div>
        </>
      }
    >
      <Stepper activeStep={activeStep} orientation={"vertical"}>
        <Step key={t("What happened")} completed={activeStep > 0}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("What happened")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {whatHappened}

              <div>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Transaction details")} completed={activeStep > 1}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Transaction details")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 0 ? (
                <TransactionsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  setSucceededStep={setSucceededStep}
                />
              ) : null}
              <div>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Customer information")} completed={activeStep > 2}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Customer information")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 1 ? (
                <CustomerDetailsStep
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  succeededStep={succeededStep}
                  setSucceededStep={setSucceededStep}
                  data={data}
                  brand={brand}
                  locale={props.locale}
                  page={"incorrect"}
                  country={country}
                />
              ) : null}
              <div>
                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Summary")} completed={activeStep > 3}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Summary")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 2 ? (
                <SummaryStep
                  setActiveStep={setActiveStep}
                  activeStep={activeStep}
                  data={data}
                  brand={brand}
                />
              ) : null}
              <div>
                <Button type="submit" variant="contained">
                  {t("Send")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
      </Stepper>
    </DisputePage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, req } = context;

  const csrfToken = setCsrfCookie(context);
  server.logPageVisit(req);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", ["common", "footer"])),
      locale,
      csrfToken,
    },
  };
}

export default PageIncorrect;
